import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/context/authStore';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag para evitar múltiples refreshes simultáneos
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Función para refrescar el token
const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = useAuthStore.getState().refreshToken;

  if (!refreshToken) {
    console.warn('[Auth] Refresh failed: No refresh token in store');
    throw new Error('No refresh token available');
  }

  console.log('[Auth] Attempting token refresh...');

  try {
    const response = await axios.post(
      `${import.meta.env.VITE_API_URL || '/api/v1'}/auth/refresh`,
      { refresh_token: refreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    );

    const { access_token, refresh_token, expires_in } = response.data;
    useAuthStore.getState().setTokens(access_token, refresh_token, expires_in);

    console.log('[Auth] Token refresh successful, new token expires in', expires_in, 'seconds');
    return access_token;
  } catch (error: any) {
    console.error('[Auth] Token refresh failed:', error.response?.status, error.response?.data || error.message);
    throw error;
  }
};

// Interceptor para agregar token y refrescar si está por expirar
api.interceptors.request.use(async (config) => {
  const { token, tokenExpiresAt, refreshToken } = useAuthStore.getState();

  // URLs que no requieren token
  const publicUrls = ['/auth/login', '/auth/refresh', '/auth/register'];
  const isPublicUrl = publicUrls.some(url => config.url?.includes(url));

  if (isPublicUrl) {
    return config;
  }

  if (token) {
    // Verificar si el token expira en menos de 2 minutos
    const expiresIn = tokenExpiresAt ? tokenExpiresAt - Date.now() : 0;
    const shouldRefresh = expiresIn < 2 * 60 * 1000; // 2 minutos

    if (shouldRefresh) {
      console.log('[Auth] Token expiring soon or expired:', Math.round(expiresIn / 1000), 'seconds remaining');
    }

    if (shouldRefresh && refreshToken && !isRefreshing) {
      isRefreshing = true;
      try {
        const newToken = await refreshAccessToken();
        config.headers.Authorization = `Bearer ${newToken}`;
        isRefreshing = false;
        processQueue(null, newToken);
      } catch (error) {
        isRefreshing = false;
        processQueue(error as Error, null);
        // Si falla el refresh, seguir con el token actual
        config.headers.Authorization = `Bearer ${token}`;
      }
    } else if (isRefreshing) {
      // Esperar a que termine el refresh actual
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: (newToken: string) => {
            config.headers.Authorization = `Bearer ${newToken}`;
            resolve(config);
          },
          reject: (err: Error) => {
            reject(err);
          },
        });
      });
    } else {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  return config;
});

// Interceptor para manejar errores de autenticación
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // No redirigir si es el endpoint de login o refresh
    const isAuthRequest = originalRequest?.url?.includes('/auth/login') ||
                          originalRequest?.url?.includes('/auth/refresh');

    if (error.response?.status === 401 && !isAuthRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = useAuthStore.getState().refreshToken;

      if (refreshToken && !isRefreshing) {
        isRefreshing = true;

        try {
          const newToken = await refreshAccessToken();
          isRefreshing = false;
          processQueue(null, newToken);

          // Reintentar la request original con el nuevo token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          processQueue(refreshError as Error, null);

          // Refresh falló - cerrar sesión
          console.warn('[Auth] Refresh failed on 401 response, logging out');
          useAuthStore.getState().logout();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      } else if (isRefreshing) {
        // Esperar a que termine el refresh actual y reintentar
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (newToken: string) => {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              resolve(api(originalRequest));
            },
            reject: (err: Error) => {
              reject(err);
            },
          });
        });
      } else {
        // No hay refresh token - cerrar sesión
        console.warn('[Auth] 401 received but no refresh token available, logging out');
        useAuthStore.getState().logout();
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export default api;
